import React from 'react';
import type { QuoteDeliveryGeo } from './useQuoteDeliveryGeo';

interface Props {
  /** The dray-rate reference fetched by useQuoteDeliveryGeo. */
  info: QuoteDeliveryGeo['drayInfo'];
  /** The dray rate currently typed in the field (number). */
  drayRate: number;
  /** Computed delivery miles (for the per-mile calculation). */
  miles: number;
}

const fmt = (n: number) => `$${n.toFixed(2)}`;

/**
 * Standard reference shown UNDER the Rate field (the live "$X.XX / mile"
 * calculation stays next to the input). Colored green when the quoted rate
 * beats the standard, red when it falls short, grey when equal / no rate yet:
 *  - PER MILE zone → "$R / mile = $T" — the standard per-mile rate and the
 *    standard dray it works out to (V1 ceil-to-$5 of rate × miles); colored by
 *    the typed rate's per-mile (rate / miles) vs the standard per-mile.
 *  - deliverystandards / FLAT band → "$X" (the flat amount) colored by the
 *    typed rate vs the standard, plus an optional "Minimum: $Y" floor.
 * Renders nothing with no match.
 */
const DrayRateHint: React.FC<Props> = ({ info, drayRate, miles }) => {
  if (!info) return null;
  const base = 'text-[0.6875rem] flex items-center gap-2 text-slate-500 dark:text-slate-400';
  const good = 'text-green-600 font-semibold dark:text-green-400';
  const bad = 'text-red-600 font-semibold dark:text-red-400';
  // Green above / red below / grey when equal or no rate typed yet.
  const compare = (value: number, standard: number): string =>
    value <= 0 || value.toFixed(2) === standard.toFixed(2) ? '' : value > standard ? good : bad;

  // PER MILE zone: "$rate / mile = $total", total = the V1 standard dray
  // (ceil(miles × rate / 5) × 5). Colored by the typed rate's per-mile vs std.
  if (info.ratetype === 'PER MILE' && info.perMileStandard != null) {
    const std = info.perMileStandard;
    const calc = miles > 0 && drayRate > 0 ? drayRate / miles : 0;
    const total = miles > 0 ? Math.ceil((miles * std) / 5) * 5 : null;
    return (
      <span className={base}>
        <span className={compare(calc, std)}>
          Standard Rate: {fmt(std)} / mile{total != null ? ` = $${total}` : ''}
        </span>
      </span>
    );
  }

  // deliverystandards / FLAT band: just the flat amount, colored by the typed
  // rate vs the standard, plus an optional minimum floor (red when below it).
  if (info.standardRate != null) {
    const belowMin = info.minimumRate != null && drayRate > 0 && drayRate < info.minimumRate;
    return (
      <span className={base}>
        <span className={compare(drayRate, info.standardRate)}>Standard Rate: {fmt(info.standardRate)}</span>
        {info.minimumRate != null && (
          <span className={belowMin ? bad : ''}>Minimum: {fmt(info.minimumRate)}</span>
        )}
      </span>
    );
  }
  return null;
};

export default DrayRateHint;
